/**
 * Email typo suggestions ("did you mean sarah@gmail.com?").
 *
 * Why: in the first weeks of the pilot, ~1 in 25 invite/share emails bounced
 * on a typo'd address (gmail.clm, misspelled local domains). The person who
 * typed it never finds out. This catches the obvious cases at entry time.
 *
 * suggestEmail(value) returns a corrected full address, or null when the
 * address looks fine (or too ambiguous to second-guess).
 */

// Domains we trust as typed — never suggest "corrections" to these
const KNOWN_GOOD = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'me.com', 'live.com', 'msn.com', 'att.net', 'bellsouth.net',
  'cox.net', 'comcast.net', 'sbcglobal.net', 'charter.net', 'protonmail.com',
  'proton.me', 'ymail.com', 'mac.com', 'mail.com',
];

// Exact domain typos seen in the wild (and their obvious neighbors)
const DOMAIN_FIXES = {
  'gmail.clm': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com', 'gmail.cim': 'gmail.com', 'gmail.om': 'gmail.com',
  'gmial.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gmal.com': 'gmail.com',
  'gmaill.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gmali.com': 'gmail.com',
  'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yahoo.co': 'yahoo.com',
  'yahoo.con': 'yahoo.com', 'yhoo.com': 'yahoo.com',
  'hotmial.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'hotmai.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com', 'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com', 'outloook.com': 'outlook.com', 'outlook.co': 'outlook.com',
  'iclould.com': 'icloud.com', 'icloud.co': 'icloud.com', 'iclod.com': 'icloud.com',
  'aol.co': 'aol.com', 'aol.con': 'aol.com',
  'bellsouth.nte': 'bellsouth.net', 'bellsouth.ent': 'bellsouth.net',
  'comcast.nte': 'comcast.net', 'cox.nte': 'cox.net', 'att.nte': 'att.net',
};

// Trailing-TLD typos, applied to any domain not otherwise recognized
const TLD_FIXES = {
  '.con': '.com', '.cmo': '.com', '.ocm': '.com', '.cpm': '.com',
  '.vom': '.com', '.xom': '.com', '.comm': '.com', '.coom': '.com',
  '.nte': '.net', '.ent': '.net',
};

export function suggestEmail(value) {
  const email = (value || '').trim().toLowerCase();
  const atIndex = email.lastIndexOf('@');
  if (atIndex < 1 || atIndex === email.length - 1) return null;

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  if (!domain.includes('.')) return null;
  if (KNOWN_GOOD.includes(domain)) return null;

  // 1. Exact known typo
  if (DOMAIN_FIXES[domain]) return `${local}@${DOMAIN_FIXES[domain]}`;

  // 2. Bad TLD on an otherwise plausible domain
  for (const [bad, good] of Object.entries(TLD_FIXES)) {
    if (domain.endsWith(bad)) {
      const fixed = domain.slice(0, -bad.length) + good;
      return `${local}@${fixed}`;
    }
  }

  // 3. One edit away from a common provider (catches the long tail)
  for (const good of KNOWN_GOOD) {
    if (Math.abs(good.length - domain.length) <= 1 && editDistanceIsOne(domain, good)) {
      return `${local}@${good}`;
    }
  }

  return null;
}

// True when a and b differ by exactly one insert, delete, or substitution
function editDistanceIsOne(a, b) {
  if (a === b) return false;
  if (Math.abs(a.length - b.length) > 1) return false;

  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < short.length && j < long.length) {
    if (short[i] === long[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (short.length === long.length) {
      i++; // substitution
    }
    j++; // insertion in the longer string (or the substituted char)
  }
  edits += long.length - j + (short.length - i);
  return edits === 1;
}

export default suggestEmail;
